import { addMarketplaceEntities, getListingsById } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { setLocalFavorites } from '../../ducks/favorites.duck';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { getWishlist } from '../../util/dataExtractors';
import { getLocalFavoritesItems, removeLocalFavoritesItems } from '../../util/favoritesHelpers';
import { parse } from '../../util/urlHelpers';
import { updateProfile, updateProfileThunk } from '../ProfileSettingsPage/ProfileSettingsPage.duck';

export const FETCH_LISTINGS_REQUEST = 'app/FavoritesPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/FavoritesPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/FavoritesPage/FETCH_LISTINGS_ERROR';

const RESULT_PAGE_SIZE = 42;

const initialState = {
  pagination: null,
  queryParams: null,
  fetchListingInProgress: false,
  fetchListingError: null,
  currentPageResultIds: [],
};

const resultIds = data => data?.data?.map(l => l.id) || [];

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        fetchListingInProgress: true,
        fetchListingError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        fetchListingInProgress: false,
        fetchListingError: null,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data?.meta || null,
      };
    case FETCH_LISTINGS_ERROR:
      return {
        ...state,
        fetchListingInProgress: false,
        fetchListingError: payload,
      };
    default:
      return state;
  }
}

const fetchListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

const fetchListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response?.data },
});

const fetchListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  payload: e,
  error: true,
});

const mergeAuthenticatedAndGuestFavorites = () => async (dispatch, getState) => {
  const state = getState();
  const isAuthenticated = state?.auth?.isAuthenticated;

  if (!isAuthenticated) {
    return getLocalFavoritesItems();
  }

  const currentUser = state?.user?.currentUser;
  const serverWishlist = getWishlist(currentUser);
  const localWishlist = getLocalFavoritesItems();

  const merged = Array.from(new Set([...(serverWishlist || []), ...(localWishlist || [])]));

  if (localWishlist.length > 0) {
    const resultAction = await dispatch(
      updateProfile({
        publicData: {
          wishlist: merged,
        },
      })
    );

    if (resultAction?.type === updateProfileThunk.rejected.type) {
      throw resultAction.payload || new Error('Failed to merge wishlist');
    }

    removeLocalFavoritesItems();
    dispatch(setLocalFavorites([]));
    await dispatch(fetchCurrentUser({ enforce: true }));
  }

  return merged;
};

// Add customMeta as an optional third parameter
export const fetchListings = (params, config, customMeta = null) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch(fetchListingsRequest(params));

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  try {
    const queryParams = {
      ...params,
      include: ['author', 'images', 'currentStock'],
      'fields.listing': ['title', 'geolocation', 'price', 'metadata', 'description', 'publicData'],
      'fields.user': [
        'profile.displayName',
        'profile.abbreviatedName',
        'profile.firstName',
        'profile.lastName',
        'profile.publicData',
      ],
      'fields.image': [
        'variants.custom',
        'variants.scaled-large',
        'variants.scaled-xlarge',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      ...createImageVariantConfig('custom', 640, 680 / 640),
      'limit.images': 1,
    };

    const response = await sdk.listings.query(queryParams);
    dispatch(addMarketplaceEntities(response));
    // If we provided custom pagination data, apply it here so the UI updates correctly
    if (customMeta) {
      response.data.meta = customMeta;
    }

    dispatch(fetchListingsSuccess(response));

    return response;
  } catch (e) {
    dispatch(fetchListingsError(storableError(e)));
    return null;
  }
};

export const loadData = (params, search, config) => async dispatch => {
  try {
    await dispatch(fetchCurrentUser());

    const queryParams = parse(search);
    // Ensure the page is treated as an integer
    const page = queryParams.page ? parseInt(queryParams.page, 10) : 1;

    const favoriteIds = await dispatch(mergeAuthenticatedAndGuestFavorites());

    if (!favoriteIds || favoriteIds.length === 0) {
      dispatch(fetchListingsSuccess({ data: { data: [], meta: { page, totalItems: 0 } } }));
      return null;
    }

    // --- NEW LOGIC: Slice IDs locally to bypass the 100 limit ---

    // 1. Calculate which chunk of up to 42 IDs we need for this page
    const startIndex = (page - 1) * RESULT_PAGE_SIZE;
    const endIndex = startIndex + RESULT_PAGE_SIZE;
    const idsForCurrentPage = favoriteIds.slice(startIndex, endIndex);

    // 2. Build the true pagination metadata for the UI
    const customMeta = {
      page,
      perPage: RESULT_PAGE_SIZE,
      totalItems: favoriteIds.length,
      totalPages: Math.ceil(favoriteIds.length / RESULT_PAGE_SIZE),
    };

    // 3. Fetch only the IDs for this page
    await dispatch(
      fetchListings(
        {
          ids: idsForCurrentPage,
          // We no longer send 'page' and 'perPage' to the API because
          // we already sliced the exact items we want!
        },
        config,
        customMeta // Pass our corrected meta data
      )
    );
  } catch (e) {
    dispatch(fetchListingsError(storableError(e)));
  }

  return null;
};
